import { isFunction, isObject } from 'lodash'
import { FormatErrors } from '.'

/** @enum {string} */
export const ValidationFields = Object.freeze({
  ACRONYM: 'acronym',
  ABBREVIATION: 'abbreviation',
  CATEGORIES: 'categories',
  COLOR: 'color',
  COMMENT: 'comment',
  COMPENSATION: 'compensation',
  COUNTRY: 'country',
  DETAILS: 'details',
  EMAIL: 'email',
  EMPLACEMENT: 'emplacement',
  FIRSTNAME: 'firstname',
  GENDER: 'gender',
  INSTITUTIONS: 'institutions',
  INSTITUTION: 'institution',
  LANGUAGE: 'language',
  LASTNAME: 'lastname',
  MANAGED: 'managed',
  NAME: 'name',
  ORIGINAL_NAME: 'originalName',
  ROLES: 'roles',
  SCHEDULES: 'schedules',
  SCHOOL: 'school',
  SECTION: 'section',
  SECTIONS: 'sections',
  SECTION_TO_YEAR: 'sectionToYear',
  SECTOR: 'sector',
  REQUEST_ORIGIN: 'requestOrigin',
  REQUEST_PERIODS: 'requestPeriods',
  DURATION: 'duration',
  TYPE: 'type',
  TYPES: 'types',
  TOTAL: 'total',
  END_DATE: 'endDate',
  START_DATE: 'startDate',
  STUDENT: 'student',
  INTERNSHIP_YEAR: 'internshipYear',
  SCHOOL_YEAR: 'schoolYear',
  SECTORS: 'sectors',
  RESUME: 'resume',
  COVER_LETTER: 'cover_letter'
})

/**
 * Password policy, aligned with the backend (a 400 otherwise) and shared by every input field:
 * LENGTH ALONE matters — at least 15 characters, no composition rule (no uppercase nor digit
 * required, see NIST SP 800-63B).
 *
 * The two units differ on purpose: the minimum is a number of CHARACTERS (an accented passphrase
 * must pass like any other), the maximum a number of BYTES, because that is what bcrypt counts —
 * beyond it, the backend encoder refuses to hash.
 */
export const PASSWORD_MIN_LENGTH = 15
export const PASSWORD_MAX_BYTES = 72

/**
 * @param {string} password
 * @returns {?string} the translation key of the problem, or null when the password is acceptable
 */
export const getPasswordPolicyError = password => {
  const value = typeof password === 'string' ? password : ''

  // [...value] counts characters instead of UTF-16 units, like the backend's mb_strlen.
  if ([...value].length < PASSWORD_MIN_LENGTH) {
    return 'password_policy.rules'
  }

  if (new TextEncoder().encode(value).length > PASSWORD_MAX_BYTES) {
    return 'password_policy.too_long'
  }

  return null
}

/**
 * @param {object} validators
 * @param {string[]} selectedKeys
 */
export const selectValidators = (validators, selectedKeys) => selectedKeys.reduce((acc, selectedKey) => {
  if (!isFunction(validators[selectedKey])) {
    throw new Error(`selectValidators: argument \`selectedKeys\` contains at least one unsupported key (${selectedKey}).`)
  }

  acc[selectedKey] = validators[selectedKey]

  return acc
}, {})

const validatorsFactory = validatorName => {
  return VALIDATOR_LIST[validatorName ?? 'DEFAULT']
}

const DEFAULT_VALIDATOR = data => {
  return { isValid: true, errors: {} }
}

const isValidSection = (section) => {
  const isValid = section.name && section.schoolYearName

  return {
    isValid: isValid,
    errors: isValid
      ? {}
      : {
        sectionId: section.name ? null : 'Section name cannot be null.',
        schoolYearId: section.schoolYearName ? null : 'Section school year cannot be null.'
      }
  }
}

/**
 * @param {object} item
 * @param {Array<{key:string, validator: (any)=>boolean} | (any) => boolean>} validators
 */
export const isValid = (item, validators) => {
  const errors = {
    keys: [],
    messages: {}
  }

  validators.forEach(validator => {
    const config = isFunction(validator) ? { key: validator.name, validator } : validator
    const report = config.validator(item)

    if (!report) {
      errors.keys.push(config.key)
      errors.messages[config.key] = [FormatErrors.CANNOT_BE_EMPTY]
    } else if ((isObject(report) && !report.valid)) {
      errors.keys.push(config.key)
      errors.messages[config.key] = report.errors
    }
  })

  return errors
}

const VALIDATOR_LIST = {
  DEFAULT: DEFAULT_VALIDATOR,
  Section: isValidSection
}

export {
  validatorsFactory,
  isValidSection
}
